import React, { useState, useRef, useEffect } from 'react';
import Input from '../../../../components/Fields/Input';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseIcon from '@mui/icons-material/Pause';
import { Skeleton } from '@mui/material';
// import { Typography } from '@mui/material';
// import { TimerComponent } from './TimerComponent';
import { SipFrequency } from './SipOptions';
import DatePickerDialog from './DatePickerDialog';
import DaySelectorDialog from './DaySelectorDialog';
import StopSipModal from './StopSipModal';
import { getRupeeWithDecimal } from '../../../../utils/numbers';
import { MetalType, MetalPriceFields } from './useMetalSip';
import useSubscriptionConstraints from '../../../../hooks/useSubscriptionConstraints';
import useSipStartDate, { DayOfWeek } from '../../../../hooks/useSipStartDate';
import useValidateSipSubscription from '../../../../hooks/useValidateSipSubscription';
import useSubscriptionDetails from '../../../../hooks/useSubscriptionDetails';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { formatMonthlyDate } from '../../DigitalSilver/SipUtils';
import { NetworkManager } from '../../../../services';
import useUserSubscription from '../../../../hooks/useUserSubscription';
import useValidateEditSubscription from '../../../../hooks/useValidateEditSubscription';

type SipFormProps = {
  frequency: SipFrequency;
  amount: number;
  onAmountChange: (amount: number) => void;
  buyingPrice?: number;
  commission?: number;
  commissionPercentage?: number;
  metalLivePrice?: any;
  metalLivePriceLoading?: boolean;
  isGramSelected: boolean;
  onSwap: () => void;
  selectedGram: number;
  onGramChange: (gram: number) => void;
  gstAmount?: number;
  onBack?: () => void;
  onGeneratePaymentLink?: (
    authLink?: string,
    errorMessage?: string,
    firstChargeDate?: string,
    scheduleDetails?: { selectedDay?: string; selectedDate?: Date | null },
  ) => void;
  client?: any;
  selectedDate?: Date | null;
  selectedDay?: string;
  isEditMode?: boolean;
  existingSipAmount?: number;
  subscriptionId?: string;
  onEdit?: (newAmount: number) => void;
  onStop?: (period?: '7' | '10' | '15' | '1' | '2' | '3' | 'PERMANENT') => void;
  metalType?: MetalType;
  refetchSubscriptions?: () => void;
  priceFields?: MetalPriceFields;
  sipStatus?: 'ACTIVE' | 'PAUSED' | null;
  existingSipStartDate?: string;
};

const SipForm: React.FC<SipFormProps> = ({
  frequency,
  amount,
  onAmountChange,
  buyingPrice,
  commission,
  commissionPercentage,
  metalLivePrice,
  metalLivePriceLoading,
  isGramSelected,
  onSwap,
  selectedGram,
  onGramChange,
  gstAmount,
  onBack,
  onGeneratePaymentLink,
  client,
  isEditMode = false,
  existingSipAmount,
  subscriptionId: propSubscriptionId,
  onEdit,
  onStop,
  metalType = 'GOLD',
  refetchSubscriptions,
  priceFields = { buyPrice: 'goldBuyPrice', sellPrice: 'goldSellPrice' },
  sipStatus,
  existingSipStartDate,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDay, setSelectedDay] = useState<string>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isDaySelectorOpen, setIsDaySelectorOpen] = useState(false);
  const [isAmountExpanded, setIsAmountExpanded] = useState(false);
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [showUpiInput, setShowUpiInput] = useState(false);
  const [upiId, setUpiId] = useState('');
  const [isStopModalOpen, setIsStopModalOpen] = useState(false);
  const [isResumingSubscription, setIsResumingSubscription] = useState(false);
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [amountInputValue, setAmountInputValue] = useState(amount > 0 ? String(amount) : '');
  const dateSelectorRef = useRef<HTMLDivElement>(null);
  const { data: subscriptionConstraints } = useSubscriptionConstraints();
  const {
    calculateStartDate,
    data: calculatedStartDate,
    isLoading: isCalculatingStartDate,
  } = useSipStartDate();
  const { data: userSubscriptionDetails } = useUserSubscription(
    client?.clientPublicId,
    metalType === 'GOLD' ? 'GOLD_SAVING' : 'SILVER_SAVING',
  );
  const { validateAsync: validateSipSubscriptionAsync, isLoading: isValidatingSipSubscription } =
    useValidateSipSubscription();
  const { validateEditSubscriptionAsync, isLoading: isValidatingEditSubscription } =
    useValidateEditSubscription();
  const { data: subscriptionDetails } = useSubscriptionDetails(
    sipStatus === 'PAUSED' ? propSubscriptionId : undefined,
  );

  // When in edit mode or PAUSED (resume flow), show payment section directly (summary view)
  // Only run when isEditMode, existingSipAmount, or sipStatus changes
  useEffect(() => {
    if (isEditMode || sipStatus === 'PAUSED') {
      setShowPaymentSection(true);
      setIsEditingAmount(false);
      if (existingSipAmount) {
        onAmountChange(existingSipAmount);
      }
    } else {
      setIsEditingAmount(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, existingSipAmount, sipStatus]);

  // Sync amount input display from parent amount, but don't overwrite when user is typing (e.g. "67.")
  useEffect(() => {
    setAmountInputValue((prev) => {
      const parsed = prev === '' || prev === '.' ? NaN : Number(prev);
      if (prev !== '' && !Number.isNaN(parsed) && parsed === amount) return prev;
      return amount > 0 ? String(amount) : '';
    });
  }, [amount]);

  // Reset editing state when switching away from edit mode
  useEffect(() => {
    if (!isEditMode) {
      setIsEditingAmount(false);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (subscriptionConstraints && subscriptionConstraints[frequency]) {
      const constraints = subscriptionConstraints[frequency];
      if (constraints) {
        const { minimumInvestment, maximumInvestment } = constraints;
        if (amount > 0 && amount < minimumInvestment) {
          setAmountError(`Minimum investment amount is ₹${minimumInvestment?.toLocaleString()}`);
        } else if (amount > maximumInvestment) {
          setAmountError(`Maximum investment amount is ₹${maximumInvestment?.toLocaleString()}`);
        } else {
          setAmountError(null);
        }
      }
    }
  }, [amount, frequency, subscriptionConstraints]);

  // Daily SIP: if today is 29th, 30th or 31st, first selectable is 1st of next month — set default
  useEffect(() => {
    if (frequency !== 'DAILY' || selectedDate !== null) return;
    if (dayjs().date() >= 29) {
      const firstOfNextMonth = dayjs().endOf('month').add(1, 'day').toDate();
      setSelectedDate(firstOfNextMonth);
      calculateStartDate({
        subscriptionFrequency: 'DAILY',
        fromDate: dayjs(firstOfNextMonth).format('YYYY-MM-DD'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency]);

  useEffect(() => {
    if (frequency === 'WEEKLY') {
      calculateStartDate({
        subscriptionFrequency: 'WEEKLY',
        fromDay: getDayForWeekly(existingSipStartDate, selectedDay).toUpperCase() as DayOfWeek,
      });
    }
    if (frequency === 'MONTHLY') {
      calculateStartDate({
        subscriptionFrequency: 'MONTHLY',
        fromDay:
          existingSipStartDate?.split('-')[2] ||
          dayjs(existingSipStartDate).format('DD') ||
          formatMonthlyDate(existingSipStartDate?.split('-')[2]),
      });
    }
  }, [frequency, calculateStartDate, existingSipStartDate]);

  const disableButtonByFrequency = (frequency: SipFrequency) => {
    if (!calculatedStartDate && frequency !== 'DAILY') return true;
    switch (frequency) {
      case 'DAILY':
        return amount === existingSipAmount;
      case 'WEEKLY':
        if (amount !== existingSipAmount) return false;
        else if (amount === existingSipAmount) {
          if (calculatedStartDate.startDate === existingSipStartDate) {
            return true;
          } else {
            const newStartDay = new Date(calculatedStartDate.startDate).getDay();
            const existingStartDay = new Date(existingSipStartDate).getDay();
            return newStartDay === existingStartDay;
          }
        }
        return true;
      case 'MONTHLY':
        if (amount !== existingSipAmount) return false;
        else if (amount === existingSipAmount) {
          return (
            calculatedStartDate?.startDate !== undefined &&
            calculatedStartDate?.startDate === existingSipStartDate
          );
        }
        return true;

      default:
        return true;
    }
  };

  // Handler for date selection - calls API immediately
  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      if (frequency === 'DAILY') {
        const formattedDate = dayjs(date).format('YYYY-MM-DD');
        calculateStartDate({
          subscriptionFrequency: 'DAILY',
          fromDate: formattedDate,
        });
      } else if (frequency === 'MONTHLY') {
        const selectedDayNumber = dayjs(date).date();
        const today = dayjs().startOf('day');
        const todayDayNumber = today.date();

        let targetDate = today.date(selectedDayNumber);
        if (selectedDayNumber < todayDayNumber) {
          targetDate = today.add(1, 'month').date(selectedDayNumber);
        }

        const lastDayOfTargetMonth = targetDate.endOf('month').date();
        if (selectedDayNumber > lastDayOfTargetMonth) {
          targetDate = targetDate.endOf('month');
        }

        calculateStartDate({
          subscriptionFrequency: 'MONTHLY',
          fromDay: targetDate.format('DD'),
        });
      }
    }
  };

  const handleDaySelect = (day: string) => {
    setSelectedDay(day);
    if (frequency === 'WEEKLY') {
      const dayInApiFormat = day.toUpperCase() as DayOfWeek;
      calculateStartDate({
        subscriptionFrequency: 'WEEKLY',
        fromDay: dayInApiFormat,
      });
    }
  };

  const handleEditClick = () => {
    setIsEditingAmount(true);
    setShowPaymentSection(false);
    if (existingSipAmount) {
      onAmountChange(existingSipAmount);
    }
    if (frequency === 'MONTHLY') {
      calculateStartDate({
        subscriptionFrequency: 'MONTHLY',
        fromDay:
          existingSipStartDate?.split('-')[2] ||
          dayjs(existingSipStartDate).format('DD') ||
          formatMonthlyDate(existingSipStartDate?.split('-')[2]),
      });
    }
  };

  const handleGeneratePaymentLink = async () => {
    if ((metalType === 'SILVER' || metalType === 'GOLD') && client?.clientPublicId) {
      try {
        const firstChargeDate = selectedDate
          ? dayjs(selectedDate).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD');

        const computedFirstChargeDate = calculatedStartDate?.startDate || firstChargeDate;

        const existingSubscriptionStartDate = userSubscriptionDetails?.subscriptions?.find(
          (subscription) => subscription.subscriptionFrequency === frequency,
        )?.startDate;

        const frequencyNote =
          frequency === 'DAILY'
            ? 'Everyday'
            : frequency === 'WEEKLY'
              ? `Weekly on ${selectedDay && !isEditMode ? selectedDay : existingSubscriptionStartDate ? dayjs(existingSubscriptionStartDate).format('dddd') : ''}`
              : `On Every ${getOnEveryMonthlyDate(selectedDate)}`;

        const response = await validateSipSubscriptionAsync({
          userId: client.userId,
          recurringAmount: amount,
          subscriptionFrequency: frequency,
          sourceType: metalType === 'GOLD' ? 'GOLD_SAVING' : 'SILVER_SAVING',
          firstChargeDate: computedFirstChargeDate,
          phone: client.phone,
          frequencyNote: frequencyNote,
        });

        const authLink =
          response?.authLink ||
          response?.data?.authLink ||
          response?.data?.data?.authLink ||
          (typeof response?.data?.data === 'string' ? response?.data?.data : null) ||
          (typeof response?.data === 'string' ? response?.data : null);
        const firstChargeDateFromResponse =
          response?.firstChargeDate ||
          response?.data?.firstChargeDate ||
          response?.data?.data?.firstChargeDate ||
          firstChargeDate;

        if (authLink && onGeneratePaymentLink) {
          const resolvedWeeklyDay =
            frequency === 'WEEKLY'
              ? selectedDay || dayjs(firstChargeDateFromResponse).format('dddd')
              : undefined;
          const resolvedMonthlyDate =
            frequency === 'MONTHLY'
              ? (selectedDate ?? dayjs(firstChargeDateFromResponse).toDate())
              : undefined;

          onGeneratePaymentLink(authLink, undefined, firstChargeDateFromResponse, {
            selectedDay: resolvedWeeklyDay,
            selectedDate: resolvedMonthlyDate,
          });
        } else {
          if (onGeneratePaymentLink) {
            onGeneratePaymentLink();
          }
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          error?.data?.message ||
          undefined;
        if (onGeneratePaymentLink) {
          onGeneratePaymentLink(undefined, errorMessage);
        }
      }
    } else {
      if (onGeneratePaymentLink) {
        onGeneratePaymentLink();
      }
    }
  };

  const getFrequencyLabel = () => {
    switch (frequency) {
      case 'DAILY':
        return 'Daily';
      case 'WEEKLY':
        return 'Weekly';
      case 'MONTHLY':
        return 'Monthly';
      default:
        return '';
    }
  };

  const getFrequencyTitle = () => {
    switch (frequency) {
      case 'DAILY':
        return 'DAILY SIP';
      case 'WEEKLY':
        return 'WEEKLY SIP';
      case 'MONTHLY':
        return 'MONTHLY SIP';
      default:
        return 'SIP';
    }
  };

  const formatDate = (date: Date) => {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const ordinal =
      day === 1 || day === 21 || day === 31
        ? 'st'
        : day === 2 || day === 22
          ? 'nd'
          : day === 3 || day === 23
            ? 'rd'
            : 'th';
    return `${day}${ordinal} ${month}, ${year}`;
  };

  const getFirstDebitText = (date: Date | string | null) => {
    const today = dayjs().startOf('day');
    const tomorrow = today.add(1, 'day');

    const getValidDate = (value?: Date | string | null) => {
      if (!value) return null;
      const day = value instanceof Date ? value : new Date(value);
      return isNaN(day.getTime()) ? null : day;
    };
    if (frequency === 'WEEKLY' || frequency === 'MONTHLY') {
      const displayDate = calculatedStartDate?.startDate
        ? dayjs(calculatedStartDate.startDate).startOf('day')
        : today;
      if (displayDate.isSame(today, 'day')) return 'today';
      if (displayDate.isSame(tomorrow, 'day')) return 'tomorrow';
      return `on ${formatDate(displayDate.toDate())}`;
    }
    const validDate = getValidDate(date);
    if (!validDate) return 'today';

    const selected = dayjs(validDate).startOf('day');

    if (selected.isSame(today, 'day')) return 'today';
    if (selected.isSame(tomorrow, 'day')) return 'tomorrow';

    return formatDate(validDate);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');

    if (isGramSelected) {
      // For grams, allow up to 4 decimal places
      if (numericValue.includes('.') && numericValue.split('.')[1]?.length > 4) {
        return;
      }
      onGramChange(numericValue ? Number(numericValue) : 0);
    } else {
      // For amount, allow up to 2 decimal places (single decimal point only)
      const parts = numericValue.split('.');
      if (parts.length > 2 || (parts[1]?.length ?? 0) > 2) {
        return;
      }
      setAmountInputValue(numericValue);
      onAmountChange(numericValue ? Number(numericValue) : 0);
    }
  };

  const getDayForWeekly = (existingSipStartDate: string, selectedDay: string) => {
    if (existingSipStartDate && selectedDay) {
      return selectedDay;
    } else if (existingSipStartDate) {
      return dayjs(existingSipStartDate).format('dddd');
    } else if (selectedDay) {
      return selectedDay;
    } else {
      return dayjs().format('dddd');
    }
  };

  const handleResumeNow = async () => {
    if (!propSubscriptionId) {
      toast.error('Subscription ID not found. Please try again.', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }

    setIsResumingSubscription(true);
    try {
      const path = NetworkManager.PATH.resumeSipSubscription.replace(
        '{{subscriptionId}}',
        propSubscriptionId,
      );
      await NetworkManager.Call({
        method: 'post',
        path,
      });

      toast.success('SIP resumed successfully', {
        duration: 2000,
        position: 'top-center',
      });

      refetchSubscriptions?.();
      onBack?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to resume SIP. Please try again.', {
        duration: 2000,
        position: 'top-center',
      });
    } finally {
      setIsResumingSubscription(false);
    }
  };

  const getOnEveryMonthlyDate = (selectedDate: Date | null) => {
    if (selectedDate && !isEditMode) {
      return formatMonthlyDate(selectedDate);
    } else {
      return (
        formatMonthlyDate(
          userSubscriptionDetails?.subscriptions?.find(
            (subscription) => subscription.subscriptionFrequency === frequency,
          )?.startDate,
        ) || formatMonthlyDate(new Date())
      );
    }
  };
  return (
    <div className="flex flex-col items-start gap-2 w-full mt-4">
      {/* Heading with Back Arrow */}
      <div className="flex items-center gap-1 w-full">
        {(onBack || showPaymentSection || showUpiInput) && (
          <button
            onClick={() => {
              if (showUpiInput) {
                // If on UPI input screen, go back to payment mode selection
                setShowUpiInput(false);
              } else if (isEditMode && isEditingAmount) {
                // If editing amount in edit mode, go back to summary view
                setIsEditingAmount(false);
                setShowPaymentSection(true);
              } else if (isEditMode && showPaymentSection && onBack) {
                onBack();
              } else if (sipStatus === 'PAUSED' && showPaymentSection && onBack) {
                // PAUSED resume flow: back goes to SIP options (no amount screen)
                onBack();
              } else if (showPaymentSection) {
                // If on payment screen, go back to investment amount screen
                setShowPaymentSection(false);
              } else if (onBack) {
                // If on investment amount screen, go back to SIP options
                onBack();
              }
            }}
            className="flex items-center justify-center p-1 hover:bg-gray-100 rounded-full transition-colors"
            type="button"
            aria-label={
              showUpiInput
                ? 'Go back to payment mode'
                : showPaymentSection
                  ? 'Go back to investment amount'
                  : 'Go back to SIP options'
            }
          >
            <ArrowBackIcon
              className="text-slate-800/80"
              style={{ width: '20px', height: '20px' }}
            />
          </button>
        )}
        <h2 className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-800/80">
          {showUpiInput ? 'UPI' : getFrequencyTitle()}
        </h2>
      </div>

      {sipStatus === 'PAUSED' && subscriptionDetails?.pausedTill && (
        <div className="w-full border border-[#E5E5E0] bg-[#D9770624] rounded-lg px-4 py-3 flex items-center gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-[#D97706] rounded-full flex items-center justify-center">
            <PauseIcon className="text-white" style={{ width: '16px', height: '16px' }} />
          </div>
          <p className="text-sm text-gray-700 flex-1">
            Your savings will automatically renew on{' '}
            {dayjs(subscriptionDetails.pausedTill).format('DD MMMM YYYY')}
          </p>
        </div>
      )}

      {/* Form Content */}
      <div className="flex flex-col items-start gap-4 w-full">
        {/* Amount Input Section - Hidden when PAUSED (resume flow shows summary card instead) or when in payment section / edit amount */}
        {((!showPaymentSection && !isEditMode) || (isEditMode && isEditingAmount)) &&
          sipStatus !== 'PAUSED' && (
            <div className="flex flex-col items-start gap-2 w-full">
              <div
                className={`flex flex-col items-start w-full bg-white border border-stone-300 overflow-hidden transition-all rounded-[6px]
              `}
              >
                {/* Header Section */}
                <div
                  className="flex flex-row justify-between items-center px-5 py-3 gap-1.5 w-full cursor-pointer relative"
                  onClick={() => setIsAmountExpanded(!isAmountExpanded)}
                >
                  <label className="text-xs font-semibold tracking-[0.05em] uppercase text-black/40">
                    ENTER {isGramSelected ? 'QUANTITY' : 'AMOUNT'} YOU WANT TO SAVE
                  </label>

                  <div className="flex items-center gap-2">
                    {/* Price valid for timer - Commented out */}
                    {/* <div className="h-fit w-fit border border-stone-300 rounded-full text-red-700 text-xs flex items-center px-2 py-[6px]">
                  Price valid for{' '}
                  {metalLivePriceLoading ? (
                    <Skeleton animation="wave" width="50px" sx={{ ml: 1 }} />
                  ) : metalLivePrice?.data?.expiresAt ? (
                    <Typography
                      sx={{
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        ml: 0.5,
                      }}
                    >
                      <TimerComponent expiresAt={metalLivePrice?.data?.expiresAt} />
                    </Typography>
                  ) : null}
                </div> */}

                    <KeyboardArrowDownIcon
                      className={`text-gray-500 transition-transform duration-200 ${
                        isAmountExpanded ? 'rotate-180' : ''
                      }`}
                      style={{ width: '20px', height: '20px' }}
                    />
                  </div>
                </div>

                {/* Content Section with Amount/Gram Input */}
                <div
                  className="flex flex-row items-end justify-between pr-5 pb-6 gap-1 w-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-row items-baseline gap-1 flex-1 min-w-0">
                    {isGramSelected ? (
                      <Input
                        value={selectedGram > 0 ? String(selectedGram) : ''}
                        onChange={handleInputChange}
                        prefixIcon={
                          <span className="text-black/40 font-medium text-xl pt-3">gms</span>
                        }
                        longPrefix={true}
                        type="text"
                        placeholder={''}
                        className="font-semibold text-[26px] leading-[150%] placeholder:text-stone-400 pt-6 border-none rounded-b-none"
                        containerClassName="border-none rounded-b-none !shadow-none flex-1"
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <div className="flex flex-col flex-1 min-w-0">
                        <Input
                          value={amountInputValue}
                          onChange={handleInputChange}
                          type="text"
                          prefixIcon={
                            <span className="text-black/40 font-medium text-xl pt-3">₹</span>
                          }
                          placeholder={''}
                          className="font-semibold text-[26px] leading-[150%] placeholder:text-stone-400 pt-6 border-none rounded-b-none"
                          containerClassName="border-none rounded-b-none !shadow-none flex-1"
                          style={{ width: '100%' }}
                        />
                        {amountError && (
                          <p className="text-xs text-red-600 font-normal mt-1 pl-3">
                            {amountError}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Display calculated amount/grams and Swap button - Swap commented out */}
                  <div className="flex items-end gap-2 mr-4 flex-shrink-0">
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm font-medium text-slate-600 whitespace-nowrap">
                        {metalLivePriceLoading ? (
                          <Skeleton width={80} animation="wave" height={20} />
                        ) : isGramSelected ? (
                          gstAmount ? (
                            `${getRupeeWithDecimal.format(gstAmount)}`
                          ) : (
                            '-'
                          )
                        ) : selectedGram ? (
                          <>
                            <span>{selectedGram}</span>
                            <span className="text-xs ml-0.5">gms</span>
                          </>
                        ) : (
                          '-'
                        )}
                      </span>
                    </div>

                    {/* Swap button - Commented out */}
                    {/* <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSwap();
                  }}
                  className="text-green-800 underline text-xs font-semibold whitespace-nowrap"
                  style={{
                    cursor: 'pointer',
                  }}
                  type="button"
                >
                  Swap
                </button> */}
                  </div>
                </div>

                {/* Expandable Details Section */}
                {isAmountExpanded && (
                  <div className="w-full bg-[#F6F6F1] border-t border-stone-300 rounded-b-[6px]">
                    {/* Live Price */}
                    {metalLivePrice?.data?.[priceFields.buyPrice] !== undefined && (
                      <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                        <span className="text-sm font-normal leading-[150%] text-slate-600">
                          Live Price
                        </span>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-sm font-medium leading-[150%] text-stone-400">
                            ₹
                          </span>
                          <span className="text-lg font-medium leading-[150%] text-slate-600">
                            {metalLivePrice.data[priceFields.buyPrice].toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Buying Price */}
                    {buyingPrice !== undefined && buyingPrice > 0 && (
                      <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                        <span className="text-sm font-normal leading-[150%] text-slate-600">
                          Buying Price
                        </span>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-sm font-medium leading-[150%] text-stone-400">
                            ₹
                          </span>
                          <span className="text-lg font-medium leading-[150%] text-slate-600">
                            {buyingPrice.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Your Commission */}
                    {commission !== undefined && (
                      <div className="px-5 py-4 flex items-center justify-between">
                        <span className="text-sm font-normal leading-[150%] text-green-700">
                          Your Commission
                        </span>
                        <div className="flex items-baseline gap-1">
                          <span className="text-lg font-semibold leading-[150%] text-green-700">
                            {parseFloat(Number(commission).toFixed(2))}
                          </span>
                          {commissionPercentage !== undefined && (
                            <span className="text-sm font-medium leading-[150%] text-stone-400 ml-1">
                              ({commissionPercentage}%)
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="text-sm font-normal text-stone-500">Amount is inclusive of 3% GST.</p>
            </div>
          )}

        {/* Date/Day Selector Section - Hidden only when payment section is shown */}
        {!showPaymentSection && !(isEditMode && frequency === 'DAILY') && (
          <div ref={dateSelectorRef} className="flex flex-col items-start gap-2 w-full">
            <div
              className="w-full bg-white border border-stone-300 rounded-md"
              style={{ borderBottom: '1px solid #D6D3D1' }}
            >
              {/* Header */}
              <div className="px-5 py-3">
                <label className="text-xs font-semibold tracking-[0.05em] uppercase text-black/40">
                  {frequency === 'WEEKLY'
                    ? 'DEBIT DAY'
                    : frequency === 'DAILY'
                      ? 'DAILY DEBIT FROM'
                      : 'MONTHLY DEBIT FROM'}
                </label>
              </div>
              {/* Content */}
              <div className="px-5 pb-6">
                {frequency === 'WEEKLY' ? (
                  <button
                    type="button"
                    onClick={() => setIsDaySelectorOpen(true)}
                    className="flex items-center justify-between w-full cursor-pointer"
                  >
                    <span className="text-sm font-normal leading-[150%] text-slate-900">
                      {
                        getDayForWeekly(
                          existingSipStartDate,
                          selectedDay,
                        ).toUpperCase() as DayOfWeek
                      }
                    </span>
                    <CalendarMonthIcon
                      className="text-slate-900/80 opacity-80"
                      style={{ width: '24px', height: '24px' }}
                    />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsDatePickerOpen(true)}
                    className="flex items-center justify-between w-full cursor-pointer"
                  >
                    {!isCalculatingStartDate ? (
                      <span className="text-sm font-normal leading-[150%] text-slate-900">
                        {frequency === 'DAILY'
                          ? selectedDate
                            ? `${dayjs(selectedDate).format('DD MMMM YYYY')}`
                            : `${existingSipStartDate ? dayjs(existingSipStartDate).format('DD MMMM YYYY') : dayjs().format('DD MMMM YYYY')}`
                          : `Monthly on ${calculatedStartDate?.startDate ? formatMonthlyDate(dayjs(calculatedStartDate?.startDate)) : formatMonthlyDate(dayjs())}`}
                      </span>
                    ) : (
                      <Skeleton width={100} animation="wave" height={20} />
                    )}
                    <CalendarMonthIcon
                      className="text-slate-900/80 opacity-80"
                      style={{ width: '24px', height: '24px' }}
                    />
                  </button>
                )}
              </div>
            </div>
            {!isCalculatingStartDate ? (
              <p className="text-sm font-normal text-stone-700">
                Your first debit is{' '}
                <span className="font-semibold">
                  {getFirstDebitText(selectedDate || selectedDay)}
                </span>
              </p>
            ) : (
              <Skeleton width={100} animation="wave" height={20} />
            )}
          </div>
        )}

        {!showPaymentSection && !(isEditMode && isEditingAmount) && (
          <button
            type="button"
            onClick={() => {
              if (sipStatus === 'PAUSED') {
                setIsStopModalOpen(true);
              } else {
                if (!amount || amount <= 0) {
                  setAmountError('Please enter a valid amount');
                  return;
                }
                setShowPaymentSection(true);
              }
            }}
            className="w-full py-4 px-5 bg-green-700 text-white font-semibold text-sm rounded-lg shadow-[0px_1.1px_1.8px_-0.3px_rgba(0,0,0,0.1)] mt-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={sipStatus !== 'PAUSED' && (!amount || amount <= 0 || !!amountError)}
          >
            {sipStatus === 'PAUSED' ? 'Resume Now' : 'Proceed'}
          </button>
        )}

        {/* Update Button - Shown when editing amount in edit mode */}
        {isEditMode && isEditingAmount && (
          <button
            type="button"
            onClick={async () => {
              if (!amount || amount <= 0) {
                return;
              }

              const subscriptionIdFromStatus = userSubscriptionDetails?.subscriptions?.find(
                (subscription) => subscription.subscriptionFrequency === frequency,
              )?.subscriptionId;

              const subscriptionId = subscriptionIdFromStatus || propSubscriptionId;

              if (!subscriptionId) {
                toast.error('Subscription ID not found. Please try again.', {
                  duration: 2000,
                  position: 'top-center',
                });
                return;
              }
              const existingSubscription = userSubscriptionDetails?.subscriptions?.find(
                (subscription) => subscription.subscriptionFrequency === frequency,
              );
              const startDateToUse =
                calculatedStartDate?.startDate ||
                existingSubscription?.startDate ||
                existingSipStartDate;
              const frequencyNote =
                frequency === 'DAILY'
                  ? 'Daily'
                  : frequency === 'WEEKLY'
                    ? `Weekly on ${selectedDay || dayjs(calculatedStartDate?.startDate || existingSipStartDate).format('dddd')}`
                    : `On Every ${calculatedStartDate?.startDate ? formatMonthlyDate(dayjs(calculatedStartDate?.startDate)) : getOnEveryMonthlyDate(selectedDate)}`;
              try {
                const response = await validateEditSubscriptionAsync({
                  subscriptionId,
                  payload: {
                    userId: client?.userId,
                    recurringAmount: amount,
                    subscriptionFrequency: frequency,
                    sourceType: metalType === 'GOLD' ? 'GOLD_SAVING' : 'SILVER_SAVING',
                    ...(frequency !== 'DAILY' && { firstChargeDate: startDateToUse }),
                    ...(frequency !== 'DAILY' && { startDate: startDateToUse }),
                    phone: client?.phone,
                    subscriptionId,
                    frequencyNote: frequencyNote,
                  },
                });

                const authLink =
                  response?.authLink ||
                  response?.data?.authLink ||
                  response?.data?.data?.authLink ||
                  (typeof response?.data?.data === 'string' ? response?.data?.data : null) ||
                  (typeof response?.data === 'string' ? response?.data : null) ||
                  (typeof response === 'string' ? response : null);
                if (authLink && onGeneratePaymentLink) {
                  const resolvedWeeklyDay =
                    frequency === 'WEEKLY'
                      ? selectedDay || getDayForWeekly(existingSipStartDate, selectedDay)
                      : undefined;
                  const resolvedMonthlyDate =
                    frequency === 'MONTHLY'
                      ? selectedDate != null
                        ? selectedDate
                        : existingSipStartDate
                          ? dayjs(existingSipStartDate).toDate()
                          : new Date()
                      : undefined;

                  onGeneratePaymentLink(authLink, undefined, undefined, {
                    selectedDay: resolvedWeeklyDay,
                    selectedDate: resolvedMonthlyDate,
                  });
                } else {
                  toast.error('Unable to get payment link. Please try again.', {
                    duration: 2000,
                    position: 'top-center',
                  });
                }
              } catch (error: any) {
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.response?.data?.error ||
                  error?.message ||
                  error?.data?.message ||
                  'Failed to update SIP. Please try again.';
                toast.error(errorMessage, {
                  duration: 2000,
                  position: 'top-center',
                });
              }
            }}
            className="w-full py-4 px-5 bg-green-700 text-white font-semibold text-sm rounded-lg shadow-[0px_1.1px_1.8px_-0.3px_rgba(0,0,0,0.1)] mt-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={
              !amount ||
              amount <= 0 ||
              isValidatingEditSubscription ||
              !!amountError ||
              disableButtonByFrequency(frequency)
            }
          >
            {isValidatingEditSubscription ? 'Updating...' : `Update ${getFrequencyLabel()} Savings`}
          </button>
        )}

        {/* Payment Section - Shown after Proceed */}
        {showPaymentSection && (
          <div className="flex flex-col items-start gap-4 w-full mt-2">
            {/* Summary Card */}
            <div
              className="flex flex-col items-start w-full bg-white border-[0.5px] border-[#E7E5E4] rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)]"
              style={{ padding: '0px' }}
            >
              {/* Amount */}
              <div className="w-full px-5 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
                <span className="text-sm font-normal leading-[150%] text-slate-600">Amount</span>
                <span className="text-sm font-medium leading-[150%] text-slate-900">
                  ₹
                  {Number(
                    (isEditMode || sipStatus === 'PAUSED'
                      ? userSubscriptionDetails?.subscriptions?.find(
                          (subscription) => subscription.subscriptionFrequency === frequency,
                        )?.amount
                      : amount > 0
                        ? amount
                        : 0) ?? 0,
                  ).toLocaleString()}
                </span>
              </div>

              {/* Auto Debit */}
              <div className="w-full px-5 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
                <span className="text-sm font-normal leading-[150%] text-slate-600">
                  Auto Debit
                </span>
                <span className="text-sm font-medium leading-[150%] text-slate-900">
                  {frequency === 'DAILY'
                    ? 'Everyday'
                    : frequency === 'WEEKLY'
                      ? `Weekly on ${selectedDay && !isEditMode ? selectedDay : dayjs(userSubscriptionDetails?.subscriptions?.find((subscription) => subscription.subscriptionFrequency === frequency)?.startDate).format('dddd')}`
                      : `On Every ${getOnEveryMonthlyDate(selectedDate)}`}
                </span>
              </div>

              {/* Validity */}
              <div className="w-full px-5 py-4 flex items-center justify-between">
                <span className="text-sm font-normal leading-[150%] text-slate-600">Validity</span>
                <span className="text-sm font-medium leading-[150%] text-slate-900">
                  Until Cancelled
                </span>
              </div>
            </div>

            {/* Action Buttons - Different for edit mode, PAUSED (resume), vs new SIP */}
            {sipStatus === 'PAUSED' ? (
              <div className="flex flex-col items-start gap-3 w-full">
                <button
                  type="button"
                  onClick={handleResumeNow}
                  disabled={isResumingSubscription}
                  className="w-full py-4 px-5 bg-green-700 text-white font-semibold text-sm rounded-lg shadow-[0px_1.1px_1.8px_-0.3px_rgba(0,0,0,0.1)] flex items-center justify-center gap-2"
                >
                  {isResumingSubscription ? 'Resuming...' : 'Resume Now'}
                </button>
              </div>
            ) : isEditMode ? (
              <div className="flex flex-col items-start gap-3 w-full">
                {/* Edit Button */}
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="w-full py-4 px-5 bg-green-700 text-white font-semibold text-sm rounded-lg shadow-[0px_1.1px_1.8px_-0.3px_rgba(0,0,0,0.1)] flex items-center justify-center gap-2"
                >
                  <EditIcon style={{ width: '20px', height: '20px' }} />
                  Edit {getFrequencyLabel()} Savings
                </button>

                {/* Stop Button */}
                <button
                  type="button"
                  onClick={() => setIsStopModalOpen(true)}
                  className="w-full py-4 px-5 bg-white text-red-600 font-semibold text-sm rounded-lg border border-stone-300 shadow-[0px_1.1px_1.8px_-0.3px_rgba(0,0,0,0.1)] flex items-center justify-center gap-2"
                >
                  <CancelIcon style={{ width: '20px', height: '20px' }} />
                  Stop {getFrequencyLabel()} Savings
                </button>
              </div>
            ) : !showUpiInput ? (
              <div className="flex flex-col items-start gap-3 w-full">
                {/* <h3 className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-800/80">
                  CHOOSE PAYMENT MODE
                </h3> */}

                {/* Approve Via UPI Button */}
                {/* <button
                  type="button"
                  onClick={() => setShowUpiInput(true)}
                  className="w-full py-4 px-5 bg-green-700 text-white font-semibold text-sm rounded-lg shadow-[0px_1.1px_1.8px_-0.3px_rgba(0,0,0,0.1)]"
                >
                  Approve Via UPI
                </button> */}

                {/* Generate Payment Link Button */}
                <button
                  type="button"
                  onClick={handleGeneratePaymentLink}
                  disabled={isValidatingSipSubscription || !amount}
                  className="w-full py-4 px-5 bg-green-700 text-white font-semibold text-sm rounded-lg border border-stone-300 shadow-[0px_1.1px_1.8px_-0.3px_rgba(0,0,0,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isValidatingSipSubscription ? 'Generating...' : 'Generate Payment Link'}
                </button>
              </div>
            ) : (
              /* UPI ID Input Section */
              <div className="flex flex-col items-start gap-4 w-full">
                <div className="flex flex-col items-start gap-2 w-full">
                  <label className="text-sm font-normal leading-[150%] text-slate-600">
                    Enter UPI ID
                  </label>
                  <Input
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    type="text"
                    placeholder="XXXXXXXXXXX@ybl"
                    className="w-full"
                    containerClassName="w-full"
                  />
                </div>

                {/* Verify Button */}
                <button
                  type="button"
                  className="w-full py-4 px-5 bg-green-700 text-white font-semibold text-sm rounded-lg shadow-[0px_1.1px_1.8px_-0.3px_rgba(0,0,0,0.1)]"
                >
                  Verify
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Date Picker Dialog */}
      <DatePickerDialog
        open={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        selectedDate={selectedDate}
        onSelectDate={handleDateSelect}
        anchorEl={dateSelectorRef.current}
        sipFrequency={frequency === 'DAILY' || frequency === 'MONTHLY' ? frequency : undefined}
      />

      {/* Day Selector Dialog */}
      <DaySelectorDialog
        open={isDaySelectorOpen}
        onClose={() => setIsDaySelectorOpen(false)}
        selectedDay={selectedDay}
        onSelectDay={handleDaySelect}
        anchorEl={dateSelectorRef.current}
      />

      {/* Stop SIP Confirmation Modal */}
      <StopSipModal
        open={isStopModalOpen}
        onClose={() => setIsStopModalOpen(false)}
        onConfirm={(period) => {
          if (onStop) {
            onStop(period);
          }
          setIsStopModalOpen(false);
        }}
        frequency={frequency}
        subscriptionId={propSubscriptionId}
        refetchSubscriptions={refetchSubscriptions}
        subscriptionStatus={sipStatus}
        onResumeSuccess={onBack}
      />
    </div>
  );
};

export default SipForm;
